//esim-portal/src/components/layout/Navbar.jsx
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar, Dropdown, Avatar } from 'flowbite-react';
import { HiOutlineGlobe, HiLogout } from 'react-icons/hi';
import { useAuth } from '../../contexts/AuthContext';
import { getUserMenuHeaderLines } from '../../utils/userMenuHeader';
import { useMediaQuery } from '../../hooks/useDeviceDetection';
import NotificationPanel from '../shared/NotificationPanel';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  // Matches Tailwind's `md` breakpoint used by the "hidden md:block" / "block md:hidden"
  // wrappers below, so only the currently visible header renders NotificationPanel.
  // Both wrappers stay mounted for layout purposes, but this keeps the polling
  // NotificationPanel instance singular instead of duplicated per tab.
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Проверка на наличие данных пользователя с учетом возможной вложенности
  const userData = user?.user || user;
  const hasUserData = userData && (userData.name || userData.email);
  const userMenuHeader = getUserMenuHeaderLines(userData, hasUserData);

  // Determine which avatar to display: Google profile picture or emoji
  const getEffectiveAvatar = () => {
    if (!userData) return null;

    // Use effectiveAvatar from API if available, otherwise determine locally
    if (userData.effectiveAvatar && userData.avatarType) {
      return {
        avatar: userData.effectiveAvatar,
        type: userData.avatarType
      };
    }

    // Fallback logic for older API responses
    const metadata = userData.metadata || {};
    const googleProfilePicture = metadata.picture;
    const avatarPreference = metadata.avatarPreference;

    // If user has Google profile picture and hasn't explicitly chosen emoji
    if (googleProfilePicture && avatarPreference !== 'emoji') {
      return {
        avatar: googleProfilePicture,
        type: 'google'
      };
    }

    return {
      avatar: userData.avatar || '😀',
      type: 'emoji'
    };
  };

  const effectiveAvatarData = getEffectiveAvatar();
  
  return (
    <div className="site-header">
      {/* Desktop layout - single row (md screens and up) */}
      <div className="hidden md:block">
        <Navbar fluid className="border-b shadow-sm bg-primary transition-colors duration-200">
          <Navbar.Brand as={Link} to="/dashboard">
            <div className="flex items-center">
              <img src="/assets/images/head_logo.svg" alt="Jetogo" className="h-8" style={{width: 'auto'}} />
            </div>
          </Navbar.Brand>

          <div className="flex order-2 items-center gap-2">
            {/* Notification Panel */}
            {isDesktop && <NotificationPanel />}
            <Dropdown
              arrowIcon={false}
              inline
              label={
                effectiveAvatarData && effectiveAvatarData.type === 'emoji' ? (
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-2xl leading-none border-2 border-purple-500 transition-colors duration-200">
                    <span className="block translate-y-[0.5px]">{effectiveAvatarData.avatar}</span>
                  </div>
                ) : effectiveAvatarData && effectiveAvatarData.type === 'google' ? (
                  <Avatar
                    alt={hasUserData ? userData.name : "User"}
                    img={effectiveAvatarData.avatar}
                    rounded
                    bordered
                    color="purple"
                    size="md"
                  />
                ) : (
                  <Avatar
                    alt={hasUserData ? userData.name : "User"}
                    img="https://flowbite.com/docs/images/people/profile-picture-5.jpg"
                    rounded
                    bordered
                    color="purple"
                    size="md"
                  />
                )
              }
            >
              <Dropdown.Header>
                {userMenuHeader.showNameRow && (
                  <span className="block text-sm text-primary transition-colors duration-200">{userMenuHeader.displayName}</span>
                )}
                <span className="block truncate text-sm font-medium text-secondary transition-colors duration-200">{userMenuHeader.displayEmail}</span>
              </Dropdown.Header>
              <Dropdown.Item as={Link} to="/profile">Profile</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/referrals">Referrals</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/game/worldcup">Promotions</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/billing">Billing</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={async () => {
                await signOut();
                navigate('/');
              }}>
                <div className="flex items-center">
                  <HiLogout className="mr-2" />
                  Sign out
                </div>
              </Dropdown.Item>
            </Dropdown>
          </div>

          {/* Navigation menu for desktop */}
          <div className="flex justify-center items-center space-x-4 order-1 flex-1">
            <Link
              to="/dashboard"
              className={`${
                location.pathname === '/dashboard' || location.pathname === '/user'
                  ? 'font-bold text-green-600 border-b-2 border-green-600'
                  : 'font-medium text-secondary hover:text-green-600 transition-colors duration-200'
              } px-2 py-2 text-sm whitespace-nowrap`}
            >
              Dashboard
            </Link>
            <Link
              to="/dashboard/esims"
              className={`${
                location.pathname === '/dashboard/esims'
                  ? 'font-bold text-green-600 border-b-2 border-green-600'
                  : 'font-medium text-secondary hover:text-green-600 transition-colors duration-200'
              } px-2 py-2 text-sm whitespace-nowrap`}
            >
              eSIMs
            </Link>
            <Link
              to="/dashboard/usage"
              className={`${
                location.pathname === '/dashboard/usage' || location.pathname === '/usage'
                  ? 'font-bold text-green-600 border-b-2 border-green-600'
                  : 'font-medium text-secondary hover:text-green-600 transition-colors duration-200'
              } px-2 py-2 text-sm whitespace-nowrap`}
            >
              Usage
            </Link>
            <Link
              to="/dashboard/billing"
              className={`${
                location.pathname === '/dashboard/billing' || location.pathname === '/billing'
                  ? 'font-bold text-green-600 border-b-2 border-green-600'
                  : 'font-medium text-secondary hover:text-green-600 transition-colors duration-200'
              } px-2 py-2 text-sm whitespace-nowrap`}
            >
              Billing
            </Link>
          </div>
        </Navbar>
      </div>

      {/* Mobile layout - two rows (sm and below) */}
      <div className="block md:hidden border-b shadow-sm bg-primary transition-colors duration-200">
        {/* Row 1: Logo, Dark Mode, Avatar */}
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center">
            <img src="/assets/images/head_logo.svg" alt="Jetogo" className="h-8" style={{width: 'auto'}} />
          </Link>

          {/* Notification Panel + Avatar */}
          <div className="flex items-center gap-2">
            {/* Notification Panel */}
            {!isDesktop && <NotificationPanel />}
            <Dropdown
              arrowIcon={false}
              inline
              label={
                effectiveAvatarData && effectiveAvatarData.type === 'emoji' ? (
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-2xl leading-none border-2 border-purple-500 transition-colors duration-200">
                    <span className="block translate-y-[0.5px]">{effectiveAvatarData.avatar}</span>
                  </div>
                ) : effectiveAvatarData && effectiveAvatarData.type === 'google' ? (
                  <Avatar
                    alt={hasUserData ? userData.name : "User"}
                    img={effectiveAvatarData.avatar}
                    rounded
                    bordered
                    color="purple"
                    size="md"
                  />
                ) : (
                  <Avatar
                    alt={hasUserData ? userData.name : "User"}
                    img="https://flowbite.com/docs/images/people/profile-picture-5.jpg"
                    rounded
                    bordered
                    color="purple"
                    size="md"
                  />
                )
              }
            >
              <Dropdown.Header>
                {userMenuHeader.showNameRow && (
                  <span className="block text-sm text-primary transition-colors duration-200">{userMenuHeader.displayName}</span>
                )}
                <span className="block truncate text-sm font-medium text-secondary transition-colors duration-200">{userMenuHeader.displayEmail}</span>
              </Dropdown.Header>
              <Dropdown.Item as={Link} to="/profile">Profile</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/referrals">Referrals</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/game/worldcup">Promotions</Dropdown.Item>
              <Dropdown.Item as={Link} to="/dashboard/billing">Billing</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={async () => {
                await signOut();
                navigate('/');
              }}>
                <div className="flex items-center">
                  <HiLogout className="mr-2" />
                  Sign out
                </div>
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>

        {/* Row 2: Navigation Menu */}
        <div className="flex justify-center items-center space-x-2 sm:space-x-4 px-4 pb-3">
          <Link
            to="/dashboard"
            className={`${
              location.pathname === '/dashboard' || location.pathname === '/user'
                ? 'font-semibold text-green-600 border-b-2 border-green-600'
                : 'text-secondary hover:text-green-600 transition-colors duration-200'
            } px-3 py-2 text-base sm:text-lg whitespace-nowrap`}
          >
            Dashboard
          </Link>
          <Link
            to="/dashboard/esims"
            className={`${
              location.pathname === '/dashboard/esims'
                ? 'font-semibold text-green-600 border-b-2 border-green-600'
                : 'text-secondary hover:text-green-600 transition-colors duration-200'
            } px-3 py-2 text-base sm:text-lg whitespace-nowrap`}
          >
            eSIMs
          </Link>
          <Link
            to="/dashboard/usage"
            className={`${
              location.pathname === '/dashboard/usage' || location.pathname === '/usage'
                ? 'font-semibold text-green-600 border-b-2 border-green-600'
                : 'text-secondary hover:text-green-600 transition-colors duration-200'
            } px-3 py-2 text-base sm:text-lg whitespace-nowrap`}
          >
            Usage
          </Link>
          <Link
            to="/dashboard/billing"
            className={`${
              location.pathname === '/dashboard/billing' || location.pathname === '/billing'
                ? 'font-semibold text-green-600 border-b-2 border-green-600'
                : 'text-secondary hover:text-green-600 transition-colors duration-200'
            } px-3 py-2 text-base sm:text-lg whitespace-nowrap`}
          >
            Billing
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Navigation;